import { useI18n } from "@core/i18n/i18n";
// ProgramId import removed - now using string type instead
import {
	sendAnalyticsEventOpenExertionTooltip,
	sendAnalyticsEventOpenMoodTooltip,
} from "@domain/program/program-analytics";
import { useFormatFeedback } from "@domain/progress/hooks/use-feedback";
import { Exertion, Mood } from "@domain/progress/patient-feedback";
import { FeedbackGraphData, getFakeFeedbackGraphData } from "@domain/progress/progress-data";
import styled from "@emotion/styled";
import { ConditionalSkeleton } from "@ui/components/common/conditional-skeleton";
import { ContentWithTooltip } from "@ui/components/common/content-with-tooltip";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { Graph, Scale } from "@ui/pages/patient-details/components/progress-tab/graph";
import { useFeedbackGraphData } from "@ui/pages/patient-details/components/progress-tab/hooks/use-progress-data";
import { physioMeMobileBreakpoint, row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { fonts } from "@ui/style/font";
import { isNil, isNotNil } from "@utils/assert";
import { Chart } from "chart.js";
import React, { useCallback, useContext, useMemo, useRef } from "react";

const NUMBER_OF_SESSIONS_DISPLAYED = 15;
const EMPTY_X_LABEL_VALUE = "";

const SCALE: { x: Scale; y: Scale } = {
	x: { min: 0, max: NUMBER_OF_SESSIONS_DISPLAYED - 1 },
	y: { min: 1, max: 6 },
};

export const FeedbackGraph: React.FC<{
	patientId: string;
	programId: string | undefined;
	isEmpty: boolean;
	isLoading: boolean;
}> = ({ patientId, programId, isEmpty, isLoading }) => {
	const { loading, feedbackGraphData } = useFeedbackGraphData(patientId, programId);
	const definedFeedbackGraphData = useMemo(() => feedbackGraphData ?? getFakeFeedbackGraphData(), [feedbackGraphData]);

	return (
		<FeedbackGraphView
			isEmpty={isEmpty}
			isLoading={
				isLoading || (feedbackGraphData === undefined && loading) || (!!programId && feedbackGraphData === undefined)
			}
			feedbackGraphData={definedFeedbackGraphData}
		/>
	);
};

const FeedbackGraphView: React.FC<{ feedbackGraphData: FeedbackGraphData; isEmpty: boolean; isLoading?: boolean }> = ({
	feedbackGraphData,
	isEmpty,
	isLoading = false,
}) => {
	const { format, formatDateShort, formatDateMedWithoutYear, formatDateFullWithoutYearWithWeekday } = useI18n();
	const dialogContext = useContext(DialogContext);
	const { formatExertion, formatMood } = useFormatFeedback();
	const skeletonProps = { active: isEmpty || isLoading, loading: isLoading };
	const graphWithoutScales = isLoading || isEmpty;
	const exertionGraphRef = useRef<{ getChart: () => Chart }>(null);
	const moodGraphRef = useRef<{ getChart: () => Chart }>(null);

	const formatExertionScaleYTooltip = useCallback(
		(v: number) => {
			const exertionWording = formatExertion(v as Exertion);
			return format("patientDetails.progress.exertionGraphSection.scaleYTooltip.v1_6", {
				title: exertionWording.title,
				description: exertionWording.description,
			});
		},
		[format, formatExertion],
	);
	const formatExertionTooltip = useCallback(
		(x: number) => {
			const session = feedbackGraphData.feedbacks[x];
			if (isNotNil(session)) {
				return (
					<div>
						<p className="p-size-s-bold">{formatDateFullWithoutYearWithWeekday(session.completionDate)}</p>
						<p className="p-size-s">
							{isNotNil(session.exertion)
								? format("patientDetails.progress.exertionGraphSection.dots.tooltip.v2_4", {
										exertion: formatExertion(session.exertion).value,
										exertionText: formatExertion(session.exertion).title,
									})
								: format("patientDetails.progress.exertionGraphSection.dots.tooltip.noData.v2_4")}
						</p>
					</div>
				);
			}
			return <></>;
		},
		[feedbackGraphData.feedbacks, format, formatDateFullWithoutYearWithWeekday, formatExertion],
	);

	const formatMoodScaleYTooltip = useCallback(
		(v: number) => {
			const moodWording = formatMood(v as Mood);
			return format("patientDetails.progress.moodGraphSection.scaleYTooltip.v1_6", {
				mood: moodWording.title,
			});
		},
		[format, formatMood],
	);

	const formatMoodValue = useCallback(
		(v: number) => {
			const moodWording = formatMood(v as Mood);
			return moodWording.icon;
		},
		[formatMood],
	);

	const formatMoodTooltip = useCallback(
		(x: number) => {
			const session = feedbackGraphData.feedbacks[x];
			if (isNotNil(session)) {
				if (session.mood) {
					const moodWording = formatMood(session.mood);
					return (
						<div>
							<p className="p-size-s-bold">{formatDateFullWithoutYearWithWeekday(session.completionDate)}</p>
							<MoodContainer>
								{moodWording.icon}
								<p className="p-size-s">{moodWording.title}</p>
							</MoodContainer>
						</div>
					);
				}
				return format("patientDetails.progress.moodGraphSection.dots.tooltip.noData", {
					date: formatDateShort(session.completionDate),
				});
			}
			return <></>;
		},
		[feedbackGraphData.feedbacks, format, formatDateShort, formatMood, formatDateFullWithoutYearWithWeekday],
	);

	const exertionData = useMemo(
		() => feedbackGraphData.feedbacks.map(session => session.exertion),
		[feedbackGraphData.feedbacks],
	);
	const moodData = useMemo(
		() => feedbackGraphData.feedbacks.map(session => session.mood),
		[feedbackGraphData.feedbacks],
	);

	const simulateHoverOnExertionDot = useCallback(
		(dataIndex: number) =>
			simulateHoverOnDot(dataIndex, exertionData[dataIndex] === null ? 1 : 0, exertionGraphRef.current?.getChart()),
		[exertionData],
	);

	const removeHoverOnExertionDotSimulation = useCallback(
		() => removeHoverOnDotSimulation(exertionGraphRef.current?.getChart()),
		[],
	);

	const simulateHoverOnMoodDot = useCallback(
		(dataIndex: number) =>
			simulateHoverOnDot(dataIndex, moodData[dataIndex] === null ? 1 : 0, moodGraphRef.current?.getChart()),
		[moodData],
	);

	const formatScaleX = useCallback(
		(xIndex: number) => {
			const feedback = feedbackGraphData.feedbacks[xIndex];
			if (isNil(feedback)) {
				return EMPTY_X_LABEL_VALUE;
			}
			return formatDateMedWithoutYear(feedback.completionDate);
		},
		[feedbackGraphData.feedbacks, formatDateMedWithoutYear],
	);

	const removeHoverOnMoodDotSimulation = useCallback(
		() => removeHoverOnDotSimulation(moodGraphRef.current?.getChart()),
		[],
	);

	return (
		<Section className={dotTooltipTarget}>
			<HeaderContainer>
				<ContentWithTooltip
					tooltip={format("patientDetails.progress.trackersGraphSection.sectionTooltip")}
					skeleton={skeletonProps.active ? skeletonProps : undefined}
				>
					<div className="p-size-m-bold">{format("patientDetails.progress.trackersGraphSection.title")}</div>
				</ContentWithTooltip>
				<ConditionalSkeleton {...skeletonProps} skeletonStyle={{ borderRadius: 4 }}>
					<Cta className="p-size-xs-semi-bold">{format("patientDetails.progress.trackersGraphSection.scaleCta")}</Cta>
				</ConditionalSkeleton>
			</HeaderContainer>
			<GraphContainer>
				<SubtitleContainer style={{ marginTop: 32 }}>
					<ContentWithTooltip
						tooltip={format("patientDetails.progress.moodGraphSection.sectionTooltip", {
							link: {
								className: "p-size-s-bold underline",
								onClick: () => {
									dialogContext?.openModal(ModalId.MoodDetails);
									sendAnalyticsEventOpenMoodTooltip();
								},
							},
						})}
						tooltipOptions={{ delay: [null, 200], interactive: true }}
						skeleton={skeletonProps.active ? skeletonProps : undefined}
					>
						<StyledHeader className="p-size-l">{format("patientDetails.progress.moodGraphSection.title")}</StyledHeader>
					</ContentWithTooltip>
				</SubtitleContainer>
				<Graph
					id="mood"
					ref={moodGraphRef}
					uiTheme="hot-reverse"
					displayLineOnHover
					displayScales={!graphWithoutScales}
					scale={SCALE}
					data={moodData}
					renderDotTooltipContent={formatMoodTooltip}
					formatScaleYTooltip={formatMoodScaleYTooltip}
					formatScaleY={formatMoodValue}
					onDataHover={simulateHoverOnExertionDot}
					onDotTooltipHide={removeHoverOnExertionDotSimulation}
					formatScaleX={formatScaleX}
				/>
				<SubtitleContainer style={{ marginTop: 12 }}>
					<ContentWithTooltip
						tooltip={format("patientDetails.progress.exertionGraphSection.sectionTooltip", {
							link: {
								className: "p-size-s-bold underline",
								onClick: () => {
									dialogContext?.openModal(ModalId.ExertionDetails);
									sendAnalyticsEventOpenExertionTooltip();
								},
							},
						})}
						tooltipOptions={{ delay: [null, 200], interactive: true }}
						skeleton={skeletonProps.active ? skeletonProps : undefined}
					>
						<StyledHeader className="p-size-l">
							{format("patientDetails.progress.exertionGraphSection.title")}
						</StyledHeader>
					</ContentWithTooltip>
				</SubtitleContainer>
				<Graph
					id="exertion"
					uiTheme="hot"
					ref={exertionGraphRef}
					displayLineOnHover
					displayScales={!graphWithoutScales}
					scale={SCALE}
					data={exertionData}
					renderDotTooltipContent={formatExertionTooltip}
					formatScaleYTooltip={formatExertionScaleYTooltip}
					onDataHover={simulateHoverOnMoodDot}
					onDotTooltipHide={removeHoverOnMoodDotSimulation}
					formatScaleX={formatScaleX}
				/>
			</GraphContainer>
		</Section>
	);
};

function simulateHoverOnDot(dataIndex: number, datasetIndex: number, chart?: Chart) {
	if (chart) {
		const chartArea = chart.chartArea;
		chart.tooltip?.setActiveElements([{ datasetIndex, index: dataIndex }], {
			x: (chartArea.left + chartArea.right) / 2,
			y: (chartArea.top + chartArea.bottom) / 2,
		});
		chart.setActiveElements([{ datasetIndex, index: dataIndex }]);
		chart.update();
	}
}

function removeHoverOnDotSimulation(chart?: Chart) {
	if (chart) {
		chart.setActiveElements([]);
		chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
		chart.update();
	}
}

const Section = styled.section`
	${stack(16, undefined, "stretch")}
`;

const HeaderContainer = styled.div`
	${row(undefined, "spacebetween", "center")};
	@media ${physioMeMobileBreakpoint} {
		flex-direction: column;
		align-items: flex-start;
		gap: 8px;
	}
`;

const Cta = styled.div`
	padding: 4px 8px;
	border-radius: 4px;
	background-color: ${colors.grey10};
	color: ${colors.grey};
`;

const StyledHeader = styled.h3`
	span {
		font-family: ${fonts.header};
	}
`;

const GraphContainer = styled.div`
	border-radius: 8px;
	border: 1px solid ${colors.grey25};
`;

const SubtitleContainer = styled.div`
	margin-left: 46px;
	margin-bottom: -12px;
	position: relative;
	z-index: 10;
`;

const MoodContainer = styled.span`
	${row(8, undefined, "center")};
`;

const dotTooltipTarget = "target-section";
